import { Modal } from "@tandemdiabetes/sugarmate-react-components"
import type { ReactNode } from "react"
import {
  useMemo,
  useCallback,
  useState,
} from "react"

import { EventModalContent } from "./EventModalContent"
import { EventModalPublicContext } from "./EventModalPublicContext"
import { useSegment } from "src/contexts/SegmentContext"
import type { ReadOnlyUserEvent } from "src/types"

type Props = {
  children?: ReactNode;
}

export function EventModal(props: Props) {
  // open states for entry modal and unsaved changes dialog
  const [
    isOpen,
    setIsOpen,
  ] = useState<boolean>(false)
  const [
    userEvent,
    setUserEvent,
  ] = useState<ReadOnlyUserEvent | undefined>(
    undefined,
  )

  const analyticsRef = useSegment()

  const openEventModal = useCallback(
    (args?: { userEvent?: ReadOnlyUserEvent }) => {
      if (args?.userEvent) {
        setUserEvent(args.userEvent)

        void analyticsRef.current.track(
          "Feed Item Opened",
          {
            "Event Type": args.userEvent.event_type,
          },
        )
      }

      setIsOpen(true)
    },
    [
      setIsOpen,
      setUserEvent,
      analyticsRef,
    ],
  )

  // a function that closes the modal
  const closeEventModal = useCallback(
    () => {
      setIsOpen(false)
    },
    [setIsOpen],
  )

  const onOpenChange = useCallback(
    // the only way to open the event modal is the openEventModal function
    (newOpen: boolean) => {
      if (!newOpen) {
        closeEventModal()
      }
    },
    [closeEventModal],
  )

  const publicContextValue = useMemo(
    () => ({
      closeEventModal,
      openEventModal,
    }),
    [
      closeEventModal,
      openEventModal,
    ],
  )

  return (
    <EventModalPublicContext.Provider value={publicContextValue}>
      <Modal
        open={isOpen}
        onOpenChange={onOpenChange}
      >
        {props.children}
        {userEvent && <EventModalContent userEvent={userEvent} />}
      </Modal>
    </EventModalPublicContext.Provider>
  )
}
